import React from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import State from '@wfp-root-app/store/state';

import AsyncTasksListExportView from '../utils/AsyncTasksListExportView';
import { ActionCreators as BeneficiaryActionCreators } from '../beneficiaries/beneficiaries';
import { ActionCreators as AsyncActionCreators, AsyncTasksTypes, AsyncTask } from '../utils/asyncTasks';
import { Filter } from '../utils/FilterTypes';
import { PagedState } from '../utils/paging';
import { RefreshSignaler } from '../utils/refresher';
import { EntitlementCategoriesConfig } from '../app/appConfig';

interface Props {
    exports: PagedState<AsyncTask>;
    entitlementTotalCount?: number;
    entitlementAtLeastTotalCount?: number;
    entitlementsFilters: Array<Filter>;
    downloadFile: (type: string, id: string) => string;
    loadAsyncTasksList: (page: number, limit: number, type: string) => Promise<RefreshSignaler>;
    exportEntitlements: (timezone: string, mapping: Array<{ key; header }>, filters: unknown) => Promise<unknown>;
    authorizeTask: (type: string, id: string) => Promise<unknown>;
    appendAsyncTasksList: (limit: number, type: string) => void;
    managerId: string;
    categories: EntitlementCategoriesConfig;
    timezone: string;
}

interface LocalState {
    selectedColumns: Record<string, boolean>;
    selectAll: boolean;
}

class EntitlementExportPage extends React.Component<Props, LocalState> {
    private availableColumns: Array<{ key: string; header: string }> = [
        { key: 'id', header: 'BB Account ID' },
        { key: 'organization', header: 'Organization' },
        { key: 'category', header: 'Category' },
        { key: 'amount', header: 'Amount' },
        { key: 'from', header: 'From Date' },
        { key: 'to', header: 'To Date' },
        { key: 'accountCreation', header: 'Account Creation Date' },
        { key: 'entitlementCreation', header: 'Entitlement Creation Date' },
        { key: 'deduplicationFlag', header: 'Deduplication Flag' },
        { key: 'deduplicationCategories', header: 'Deduplication Categories' },
        { key: 'removedByAsyncTaskId', header: 'Removal Task ID' },
        { key: 'removalReason', header: 'Removal reason' },
    ];

    private defaultPageSize = 10;
    private refresher?: RefreshSignaler;

    constructor(props: Props) {
        super(props);
        this.state = {
            selectedColumns: {
                id: true,
                householdId: true,
            },
            selectAll: false,
        };
    }

    async changePageRequested() {
        this.props.appendAsyncTasksList(this.defaultPageSize, AsyncTasksTypes.EXPORT_BENEFICIARIES_ENTITLEMENTS);
    }

    async componentDidMount() {
        this.refresher = await this.props.loadAsyncTasksList(
            1,
            this.defaultPageSize,
            AsyncTasksTypes.EXPORT_BENEFICIARIES_ENTITLEMENTS
        );
    }

    componentWillUnmount() {
        if (this.refresher) {
            this.refresher.stop();
        }
    }

    renderExportFeature() {
        return (
            <div>
                <div className="wfp-form--group">
                    <div className="checkbox" key="all" style={{ marginBottom: 25 }}>
                        <label>
                            <input
                                checked={this.state.selectAll}
                                data-testid="export.columns.selectAll"
                                name="all"
                                onChange={this._handleCheckAll.bind(this)}
                                type="checkbox"
                            />{' '}
                            Select all
                        </label>
                    </div>
                    {this.availableColumns.map((column) => (
                        <div className="checkbox" key={column.key}>
                            <label>
                                <input
                                    checked={this.state.selectedColumns[column.key] || false}
                                    data-testid={`export.column.${column.key}`}
                                    name={column.key}
                                    onChange={this._handleCheck.bind(this)}
                                    type="checkbox"
                                />{' '}
                                {column.header}
                            </label>
                        </div>
                    ))}
                </div>

                <div className="wfp-form--actions">
                    <button className="wfp-btn" data-testid="export.generate" type="submit">
                        Generate
                    </button>
                </div>
            </div>
        );
    }

    renderExportFeatureDescription() {
        return (
            <div>
                {this.props.entitlementTotalCount !== null && this.props.entitlementTotalCount !== undefined && (
                    <p>{this.props.entitlementTotalCount.toLocaleString('en-US')} entitlements listed.</p>
                )}

                {(this.props.entitlementTotalCount === null || this.props.entitlementTotalCount === undefined) &&
                    this.props.entitlementAtLeastTotalCount !== null &&
                    this.props.entitlementAtLeastTotalCount !== undefined && (
                        <p>
                            At least {this.props.entitlementAtLeastTotalCount.toLocaleString('en-US')} entitlements
                            listed.
                        </p>
                    )}
                <p>Please select the information you would like to download per Entitlement:</p>
            </div>
        );
    }

    render() {
        const title = 'Download Assistance Provided';

        return (
            <main>
                <nav className="wfp-breadcrumbs">
                    <ol className="breadcrumbs--wrapper">
                        <li className="breadcrumbs--item">
                            <Link className="breadcrumbs--link" to="/home">
                                <span>Home</span>
                            </Link>
                        </li>
                        <li className="breadcrumbs--item">
                            <Link className="breadcrumbs--link" to="/entitlements/download">
                                <span>File Download</span>
                            </Link>
                        </li>
                        <li className="breadcrumbs--item">
                            <span className="breadcrumbs--last">{title}</span>
                        </li>
                    </ol>
                </nav>
                <h3>{title}</h3>
                {this.renderExportFeatureDescription()}
                <form onSubmit={this._exportEntitlements.bind(this)}>{this.renderExportFeature()}</form>
                {this.props.exports && this.props.exports.items.length > 0 && (
                    <div>
                        <AsyncTasksListExportView
                            authorize={this.props.authorizeTask.bind(this)}
                            authorizedManagerId={this.props.managerId}
                            canDownload={true}
                            data={this.props.exports.items}
                            downloadFile={this.props.downloadFile.bind(this)}
                        />
                        <div className="wfp-form--actions">
                            <button
                                className="wfp-btn--primary"
                                disabled={this.props.exports.items.length === this.props.exports.paging.total}
                                onClick={this.changePageRequested.bind(this)}
                                type="button"
                            >
                                More
                            </button>
                        </div>
                    </div>
                )}
            </main>
        );
    }

    _handleCheck(event) {
        const key = event.target.name;
        const value = event.target.checked;

        this.setState((prevState) => {
            const selectedColumns = prevState.selectedColumns;
            selectedColumns[key] = value;
            return { selectedColumns };
        });
    }

    _handleCheckAll(event) {
        const value = event.target.checked;
        const selectedColumns = {};

        this.availableColumns.forEach((column) => {
            selectedColumns[column.key] = value;
        });

        this.setState(() => {
            return { selectedColumns, selectAll: value };
        });
    }

    _exportEntitlements(event) {
        event.preventDefault();
        const mapping = this.availableColumns.filter((column) => this.state.selectedColumns[column.key]);

        return this.props
            .exportEntitlements(this.props.timezone, mapping, this.props.entitlementsFilters)
            .then(() =>
                this.props.loadAsyncTasksList(
                    1,
                    this.defaultPageSize,
                    AsyncTasksTypes.EXPORT_BENEFICIARIES_ENTITLEMENTS
                )
            );
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapDispatchToProps(dispatch: any) {
    return {
        exportEntitlements: bindActionCreators(BeneficiaryActionCreators.exportEntitlements, dispatch),
        loadAsyncTasksList: bindActionCreators(AsyncActionCreators.loadAsyncTasksList, dispatch),
        appendAsyncTasksList: bindActionCreators(AsyncActionCreators.appendAsyncTasksList, dispatch),
        authorizeTask: bindActionCreators(AsyncActionCreators.authorizeAsyncTask, dispatch),
        downloadFile: bindActionCreators(AsyncActionCreators.downloadFile, dispatch),
    };
}

function mapStateToProps(state: State) {
    return {
        entitlementTotalCount: state.entitlements && state.entitlements.paging && state.entitlements.paging.total,
        entitlementAtLeastTotalCount:
            state.entitlements && state.entitlements.paging && state.entitlements.paging.totalAtLeast,
        exports: state.asyncTasks.asyncTasks[AsyncTasksTypes.EXPORT_BENEFICIARIES_ENTITLEMENTS] || new PagedState(),
        entitlementsFilters: state.entitlements && state.entitlements.filters,
        managerId: state.auth.manager ? state.auth.manager.id : null,
        categories: state.appConfig.entitlementsConfig,
        timezone: state.appConfig.timeZone,
    };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default connect(mapStateToProps, mapDispatchToProps, null)(EntitlementExportPage as any);
